import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { getValuesFromEnum } from "@app/class/Utilities";
import { CustomFormValidators } from "@app/directives/_forms/form-validation.service";
import { VehicleType } from "@app/enum/VehicleType.enum";
import { IVehicle } from "@app/interfaces/IVehicle";
import { FoundationVehiclesService } from "@app/main/modules/foundation/_services/vehicle.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

@UntilDestroy()
@Component({
    selector: 'add-vehicle',
    template: require('./add-vehicle.component.html')
})
export class AddVehicleComponent implements OnInit, OnChanges {

    @Input() accountId: number;
    @Input() vehicle: IVehicle; // For editing existing vehicles
    @Input() showVehicleType: boolean;
    @Input() vehicleReg: string; // For new vehicles, eg. when a user has entered a Reg in an autocomplete and it was not found.
    @Output() vehicleSave = new EventEmitter<IVehicle>();
    @Output() cancel = new EventEmitter<void>();

    VehicleTypes = VehicleType;
    vehicleTypes = getValuesFromEnum(VehicleType).filter(vType => vType != VehicleType.Unknown);
    selectedVehicleType: VehicleType;

    vehicleForm: FormGroup;

    vehicles: IVehicle[];

    editMode: boolean;
    loading: boolean;
    saving: boolean;

    constructor(
        private readonly vehicleService: FoundationVehiclesService) { }
        
    ngOnInit(): void {
        this.loadVehicles();
        this.createForm();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['vehicle'] && this.vehicle) {
            this.editMode = true;
        }
    }


    saveVehicle(): void {
        this.saving = true;
        this.vehicleService.createUpdateVehicle(this.vehicle)
            .pipe(untilDestroyed(this))
            .subscribe(vehicle => {
                this.vehicleSave.emit(vehicle);
                this.saving = false;
            })
    }

    cancelNewVehicle(): void {
        this.cancel.emit();
    }

    onVehicleTypeChanged(value: VehicleType): void {
        this.vehicleForm.get('VehicleType').setValue(value);
        this.selectedVehicleType = value;
    }

    //--

    private loadVehicles(): void {
        this.loading = true;
        this.vehicleService.getVehiclesForAccount(this.accountId)
            .pipe(
                untilDestroyed(this)
            )
            .subscribe(vehicles => {
                this.vehicles = vehicles;
                this.createForm();
                this.loading = false;
            })
    }

    private createForm(): void {
        if (this.editMode) {
            this.createVehicleEditForm();
        }
        else {
            this.createVehicleAddForm();
        }
    }

    private createVehicleAddForm(): void {

        this.vehicleForm = new FormGroup({
            "Reg": new FormControl(this.vehicleReg, [Validators.required, CustomFormValidators.duplicateVehicle(this.vehicles), CustomFormValidators.emptyWhiteSpace], CustomFormValidators.duplicateVehicleFromDatabaseAsync(this.vehicleService, this.accountId)),
            "Make": new FormControl(""),
            "Model": new FormControl(""),
            "VehicleType": new FormControl(this.vehicle?.VehicleType, [Validators.required]),
        });


        this.vehicleForm.statusChanges.pipe(untilDestroyed(this)).subscribe((status: string) => {
            if (status === "INVALID") {
                return;
            }

            this.vehicle = {
                ...this.vehicle,
                AccountId: this.accountId,
                Reg: this.vehicleForm.value.Reg,
                ShortReg: this.vehicleForm.value.Reg,
                Make: this.vehicleForm.value.Make,
                Model: this.vehicleForm.value.Model,
                VehicleType: this.vehicleForm.value.VehicleType
            }
        })
    }

    private createVehicleEditForm(): void {
        if (!this.vehicle) {
            return;
        }

        this.vehicleForm = new FormGroup({
            "Reg": new FormControl(this.vehicle.Reg, [Validators.required, CustomFormValidators.duplicateVehicle(this.vehicles), CustomFormValidators.emptyWhiteSpace]),
            "ShortReg": new FormControl(this.vehicle.ShortReg),
            "Make": new FormControl(this.vehicle.Make),
            "Model": new FormControl(this.vehicle.Model),
            "VehicleType": new FormControl(this.vehicle?.VehicleType)
        });


        this.vehicleForm.statusChanges.pipe(untilDestroyed(this)).subscribe((status: string) => {
            if (status === "INVALID") {
                return;
            }

            this.vehicle = {
                ...this.vehicle,
                Reg: this.vehicleForm.value.Reg,
                ShortReg: this.vehicleForm.value.ShortReg,
                Make: this.vehicleForm.value.Make,
                Model: this.vehicleForm.value.Model,
                VehicleType: this.vehicleForm.value.VehicleType
            }
        })
    }

}
